import { Injectable } from '@angular/core';
import {PushNotifications, PushNotificationSchema} from '@capacitor/push-notifications';
import { HttpClient } from '@angular/common/http';
import { from, Subject } from 'rxjs';
import { AlertController, Platform } from '@ionic/angular';
import { Router } from '@angular/router';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getApps, getApp, initializeApp } from 'firebase/app';
import * as uuid from 'uuid';
import { AbstractService } from './abstract.service';
import { FavoritoService } from './favorito.service';
import { Browser } from '@capacitor/browser';
import { environment } from '../../../environments/environment';

const BD_MONITORAR_NOTIFICACAO = '_bd_monitorar_notificacao';
const TB_MONITORAR_NOTIFICACAO = '_tb_monitorar_notificacoes';
const NOTIFICACOES_PROCESSADAS = '_monitorar_notificacoes_processadas';
export const MONITORAR_TOKEN_FIREBASE = '_monitorar_token_firebase';

@Injectable({
  providedIn: 'root'
})
export class NotificacaoService extends AbstractService<any> {
  private notificacoes$ = new Subject<any[]>();
  private estacoesFavoritas = [];

  constructor(
    public http: HttpClient,
    private alertCtrl: AlertController,
    private platform: Platform,
    private afMessaging: AngularFireMessaging,
    private router: Router,
    private favoritoService: FavoritoService) {
    super('v1/notificacoesCidadao', http);
  }

  async alertaNotificacao(notificacao: any) {
    const isPrincipal = notificacao.tipo === 'PRINCIPAL';
    const alert = await this.alertCtrl.create({
      header: 'Notificação',
      mode: 'ios',
      subHeader: notificacao.titulo,
      message: notificacao.mensagem,
      cssClass: isPrincipal ? 'alert-notificacao-principal' : '',
      buttons: [ 'OK' ]
    });

    await alert.present();
  }

  async registrarDispositivoWeb() {
    if (!this.platform.is('hybrid')) {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          console.error('[PWA] Permissão de notificação negada');
          return;
        }

        const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        await navigator.serviceWorker.ready;

        const app = getApps().length ? getApp() : initializeApp(environment.firebaseConfig);
        const messaging = getMessaging(app);
        const token = await getToken(messaging, {
          vapidKey: (environment.firebaseConfig as any).vapidKey,
          serviceWorkerRegistration: swRegistration
        });

        console.log('[PWA] FCM Token:', token);
        localStorage.setItem(MONITORAR_TOKEN_FIREBASE, token);

        if (token) {
          this.cadastrarDispositivo(token);
          onMessage(messaging, (payload: any) => {
            console.log('[PWA] Mensagem recebida em foreground:', payload);
            const notificacao = { ...(payload.notification || {}), tipo: payload.data?.tipo || '' };
            this.createDB(notificacao);
            if (notificacao.tipo === 'PRINCIPAL') {
              this.alertaNotificacao({
                titulo: notificacao.title,
                mensagem: notificacao.body,
                tipo: notificacao.tipo,
                dataRecebimento: new Date().toISOString(),
              });
            }
          });
        }
      } catch (error) {
        console.error('[PWA] Erro ao registrar dispositivo para push:', error);
      }
      return;
    }
  }

  async createDB(data) {
    const request = await indexedDB.open(BD_MONITORAR_NOTIFICACAO);
    request.onsuccess = (event: any) => {
      const db = event.target.result;
      const version = db.version;
      db.close();
      this.addMessageDB(data, version);
    };
  }

  async addMessageDB(data, version) {
    const request = await indexedDB.open(BD_MONITORAR_NOTIFICACAO, version + 1);
    request.onupgradeneeded = (event: any) => {
      const db = event.target.result;
      let store: any;
      const notificacao = { ...data };
      notificacao.id = uuid.v4();
      notificacao.data = new Date();
      if (!db.objectStoreNames.contains(TB_MONITORAR_NOTIFICACAO)) {
        store = db.createObjectStore(TB_MONITORAR_NOTIFICACAO);
        store.add(notificacao, notificacao.id);
      } else {
        const transaction = event.target.transaction;
        store = transaction.objectStore(TB_MONITORAR_NOTIFICACAO);
        store.add(notificacao, notificacao.id);
      }
    };
    request.onsuccess = (event: any) => {
      const db = event.target.result;
      const transaction = db.transaction(TB_MONITORAR_NOTIFICACAO, 'readonly');
      const consultar = transaction.objectStore(TB_MONITORAR_NOTIFICACAO).getAll();
      consultar.onsuccess = () => {
        this.notificacoes$.next(consultar.result);
        db.close();
      };
    };
  }

  removeMessageStorage(notificacao: any) {
    const request = indexedDB.open(BD_MONITORAR_NOTIFICACAO);
    request.onsuccess = async (event: any) => {
      const db = event.target.result;
      if (db.objectStoreNames.contains(TB_MONITORAR_NOTIFICACAO)) {
        const transaction = db.transaction(TB_MONITORAR_NOTIFICACAO, 'readwrite');
        const data = transaction.objectStore(TB_MONITORAR_NOTIFICACAO).delete(notificacao.id);
        data.onsuccess = () => {
          const consultar = transaction.objectStore(TB_MONITORAR_NOTIFICACAO).getAll();
          consultar.onsuccess = () => {
            this.notificacoes$.next(consultar.result);
            db.close();
          };
        };
      }
    };
  }

  getNotificacoes() {
    const request: any = indexedDB.open(BD_MONITORAR_NOTIFICACAO);
    request.onsuccess = async (event: any) => {
      const db = event.target.result;
      if (db.objectStoreNames.contains(TB_MONITORAR_NOTIFICACAO)) {
        const transaction = db.transaction(TB_MONITORAR_NOTIFICACAO, 'readwrite');
        const result = transaction.objectStore(TB_MONITORAR_NOTIFICACAO).getAll();
        result.onsuccess = () => {
          this.notificacoes$.next(result.result);
          db.close();
        };
      } else {
        this.notificacoes$.next([]);
        db.close();
      }
    };
    return this.notificacoes$.asObservable();
  }

  cadastrarDispositivo(token: string) {
    // tslint:disable-next-line:no-debugger
    // debugger;
    from(this.favoritoService.init()).subscribe(estacoes => {
      const principal = estacoes.find(x => x.isPrincipal);
      const favoritas = estacoes.filter(x => !x.isPrincipal).map(x => x.idEstacao);
      const dispositivo = {
        ativo: true,
        idEstacao: principal ? principal.idEstacao : null,
        idsEstacoesFavoritas: favoritas,
        token
      };
      this.post(dispositivo).subscribe(() => {
        console.log('Dispositivo cadastrado com sucesso.');
      });
    });
  }

  async startNotificacao(notRetry?: boolean) {
    if (!this.platform.is('hybrid')) {
      return this.registrarDispositivoWeb();
    }
    // PushNotifications.register().then(() => console.log('push registrado'));
    PushNotifications
      .requestPermissions()
      .then(permission => {
        if (permission.receive === 'granted') {
          this.notificacaoListeners();
          PushNotifications.register();
          this.sincronizarNotificacoesEntregues();
          this.platform.resume.subscribe(() => this.sincronizarNotificacoesEntregues());
        } else {
          // android workaround
          if (!notRetry) {
            this.startNotificacao(true);
          }
        }
      })
      .then(() => console.log('push registrado'));
  }

  private notificacaoListeners() {
    PushNotifications.addListener('registration',
      (token) => {
        console.log('PushNotificationToken', token);
        localStorage.setItem(MONITORAR_TOKEN_FIREBASE, token.value);
        console.log('Notificacao registration success, token: ' + token.value);
        this.cadastrarDispositivo(token.value);
      }
    );

    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError',
      (error: any) => {
        console.error('Error on registration: ' + JSON.stringify(error));
      }
    );

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener('pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        const tipo = (notification.data as any)?.tipo || '';
        this.salvarNotificacao(notification);
        if (tipo === 'PRINCIPAL') {
          // @ts-ignore
          this.alertaNotificacao({
            id: notification.id,
            titulo: notification.title,
            mensagem: notification.body,
            tipo,
            dataRecebimento: new Date().toISOString(),
            lido: false
          });
        }
      }
    );

    // Method called when tapping on a notification
    PushNotifications.addListener('pushNotificationActionPerformed',
      (action) => {
        this.salvarNotificacao(action.notification);
        this.router.navigate([ '/tabs/painel' ]);
        if (action.notification.data.link) {
          this.abrirLink(action.notification.data.link);
        }
      }
    );
  }

  // Notificações exibidas pelo sistema com o app em segundo plano não disparam
  // pushNotificationReceived; ao abrir/retomar o app, salvamos as que ainda estão na bandeja.
  async sincronizarNotificacoesEntregues() {
    if (!this.platform.is('hybrid')) {
      return;
    }
    try {
      const entregues = await PushNotifications.getDeliveredNotifications();
      entregues.notifications.forEach(n => this.salvarNotificacao(n));
    } catch (error) {
      console.error('Erro ao sincronizar notificações entregues:', error);
    }
  }

  private salvarNotificacao(notification: PushNotificationSchema) {
    const data: any = notification.data || {};
    const tipo = data.tipo || '';
    // Em segundo plano o title/body ficam com o sistema; o backend duplica o texto no data.
    const title = notification.title || data.titulo || '';
    const body = notification.body || data.mensagem || '';
    if (!title && !body) {
      return;
    }
    const chave = `${title}|${body}|${data['google.sent_time'] || notification.id || ''}`;
    const processadas: string[] = JSON.parse(localStorage.getItem(NOTIFICACOES_PROCESSADAS) || '[]');
    if (processadas.includes(chave)) {
      return;
    }
    processadas.push(chave);
    localStorage.setItem(NOTIFICACOES_PROCESSADAS, JSON.stringify(processadas.slice(-100)));
    this.createDB({ ...notification, title, body, tipo });
  }

  async abrirLink(link: string) {
    const alert = await this.alertCtrl.create({
      header: 'Deseja abrir o link?',
      message: '<p>Você será redirecionado para o navegador.</p>',
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel',
          cssClass: 'secondary'
        }, {
          text: 'Confirmar',
          handler: async () => {
            await Browser.open({ url: link });
          }
        }
      ]
    });

    await alert.present();
  }
}
